import React, { JSX } from 'react';

import {
  RADIUS_SCALE,
  COLOR_SCALE,
  SIZE_SCALE,
  GAP_SCALE,
  DISPLAY,
  FLEX,
  SPACE_X_SCALE,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

interface SkeletonProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  className?: string | undefined;
  variant?: 'default' | 'text' | 'title' | 'avatar' | 'button' | 'card' | 'rectangle' | 'circle';
  lines?: number;
  width?: string;
  height?: string;
  animate?: boolean;
}

// Enhanced skeleton variants for different content types
const getSkeletonVariant = (
  variant: 'default' | 'text' | 'title' | 'avatar' | 'button' | 'card' | 'rectangle' | 'circle'
) => {
  switch (variant) {
    case 'default':
      return `${RADIUS_SCALE.md} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
    case 'text':
      return `${SIZE_SCALE['4']} ${RADIUS_SCALE.sm} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
    case 'title':
      return `${SIZE_SCALE['6']} ${SIZE_SCALE.w3_4} ${RADIUS_SCALE.sm} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
    case 'avatar':
      return `${SIZE_SCALE['12']} ${SIZE_SCALE.w12} ${RADIUS_SCALE.full} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
    case 'button':
      return `${SIZE_SCALE['10']} ${SIZE_SCALE.w20} ${RADIUS_SCALE.md} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
    case 'card':
      return `${SIZE_SCALE['32']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
    case 'rectangle':
      return `${RADIUS_SCALE.md} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
    case 'circle':
      return `${RADIUS_SCALE.full} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
    default:
      return `${RADIUS_SCALE.md} ${COLOR_SCALE['skeleton-bg']} shimmer-effect`;
  }
};

function Skeleton({
  className,
  variant = 'default',
  lines = 1,
  width,
  height,
  animate = true,
  ...props
}: SkeletonProps): JSX.Element {
  // For multi-line text skeletons
  if (variant === 'text' && lines > 1) {
    return (
      <div className={cn(GAP_SCALE['2'], 'flex flex-col')} {...props}>
        {Array.from({ length: lines }, (_, index) => (
          <div
            key={index}
            className={cn(
              getSkeletonVariant('text'),
              {
                'w-full': index === lines - 1, // Last line shorter
                'w-5/6': index === lines - 2 && lines > 2, // Second to last line medium
                'w-4/6': index < lines - 2, // Earlier lines shorter
              },
              !animate && 'shimmer-effect-off',
              className
            )}
            style={{ width, height }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(getSkeletonVariant(variant), !animate && 'shimmer-effect-off', className)}
      style={{ width, height }}
      {...props}
    />
  );
}

// Pre-built skeleton components for common patterns
const SkeletonText = ({
  lines = 3,
  className,
  ...props
}: Omit<SkeletonProps, 'variant'> & { lines?: number }) => (
  <Skeleton variant="text" lines={lines} className={className} {...props} />
);

const SkeletonTitle = ({ className, ...props }: Omit<SkeletonProps, 'variant'>) => (
  <Skeleton variant="title" className={className} {...props} />
);

const SkeletonAvatar = ({ className, ...props }: Omit<SkeletonProps, 'variant'>) => (
  <Skeleton variant="avatar" className={className} {...props} />
);

const SkeletonButton = ({ className, ...props }: Omit<SkeletonProps, 'variant'>) => (
  <Skeleton variant="button" className={className} {...props} />
);

const SkeletonCard = ({
  className,
  children,
  ...props
}: SkeletonProps & { children?: React.ReactNode }) => (
  <div className={cn('p-4 space-y-3', className)} {...props}>
    {children ?? (
      <>
        <SkeletonAvatar className="mb-2" />
        <SkeletonTitle />
        <SkeletonText lines={2} />
      </>
    )}
  </div>
);

const SkeletonList = ({
  count = 3,
  className,
  ...props
}: Omit<SkeletonProps, 'variant'> & { count?: number }) => (
  <div className={cn('space-y-3', className)} {...props}>
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className={`${DISPLAY.flex} ${FLEX.flexItemsCenter} ${SPACE_X_SCALE['3']}`}>
        <SkeletonAvatar width="40px" height="40px" />
        <div className="flex-1 space-y-2">
          <SkeletonTitle width="60%" height="16px" />
          <SkeletonText lines={1} width="80%" />
        </div>
      </div>
    ))}
  </div>
);

export {
  Skeleton,
  SkeletonText,
  SkeletonTitle,
  SkeletonAvatar,
  SkeletonButton,
  SkeletonCard,
  SkeletonList,
};
